import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  decideMatchReview,
  getProbabilityModel,
  getRemovalEffects,
  getSignalCoverage,
  getAccountDossier,
  getChannelsPage,
  getHotAccounts,
  getMatchReviews,
  getSignalOpsHealth,
  getWeightContrast,
  runSignalOpsPipeline,
  getFunnelOverview,
  getFunnelAccounts,
  getStageHistory,
  setStageOverride,
  clearStageOverride,
  type FunnelStage,
  type FunnelAccountsParams,
  getPoints,
  setPoints,
  setScoreSetting,
} from '../api/signalOps';

export function useHotAccounts(params: { limit?: number; band?: string; icpFit?: string } = {}) {
  return useQuery({
    queryKey: ['signal-ops', 'accounts', params],
    queryFn: () => getHotAccounts(params),
  });
}

export function useAccountDossier(id: string | null) {
  return useQuery({
    queryKey: ['signal-ops', 'account', id],
    queryFn: () => getAccountDossier(id!),
    enabled: !!id,
  });
}

export function useChannelsPage(periodDays: number) {
  return useQuery({
    queryKey: ['signal-ops', 'channels', periodDays],
    queryFn: () => getChannelsPage(periodDays),
  });
}

export function useSignalOpsHealth() {
  return useQuery({
    queryKey: ['signal-ops', 'health'],
    queryFn: getSignalOpsHealth,
  });
}

export function useMatchReviews() {
  return useQuery({
    queryKey: ['signal-ops', 'matches'],
    queryFn: getMatchReviews,
  });
}

export function useWeightContrast() {
  return useQuery({
    queryKey: ['signal-ops', 'weights', 'contrast'],
    queryFn: getWeightContrast,
  });
}

export function useDecideMatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'accepted' | 'rejected' }) =>
      decideMatchReview(id, decision),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['signal-ops', 'matches'] });
      void qc.invalidateQueries({ queryKey: ['signal-ops', 'health'] });
    },
  });
}

/**
 * Runs the whole pipeline. Long — identity resolution walks every synced
 * company — so there is deliberately no optimistic UI; the caller shows the
 * per-stage counts it returns, including any failed stages.
 */
export function useRunPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: runSignalOpsPipeline,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['signal-ops'] });
    },
  });
}

export function useProbabilityModel() {
  return useQuery({ queryKey: ['signal-ops', 'model', 'probabilities'], queryFn: getProbabilityModel });
}

export function useRemovalEffects() {
  return useQuery({ queryKey: ['signal-ops', 'model', 'removal-effects'], queryFn: getRemovalEffects });
}

export function useSignalCoverage() {
  return useQuery({ queryKey: ['signal-ops', 'coverage'], queryFn: getSignalCoverage });
}

export function useFunnelOverview(periodDays = 180) {
  return useQuery({ queryKey: ['signal-ops', 'funnel', 'overview', periodDays], queryFn: () => getFunnelOverview(periodDays) });
}

export function useFunnelAccounts(params: FunnelAccountsParams) {
  return useQuery({ queryKey: ['signal-ops', 'funnel', 'accounts', params], queryFn: () => getFunnelAccounts(params) });
}

export function useStageHistory(companyId: string | null) {
  return useQuery({
    queryKey: ['signal-ops', 'funnel', 'history', companyId],
    queryFn: () => getStageHistory(companyId!),
    enabled: !!companyId,
  });
}

/** Accept, recycle or disqualify by hand. Errors are left on the mutation for the caller to show. */
export function useStageOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { companyId: string; stage: FunnelStage; reason: string } | { companyId: string; clear: true }) =>
      'clear' in v ? clearStageOverride(v.companyId) : setStageOverride(v.companyId, v.stage, v.reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['signal-ops', 'funnel'] });
    },
  });
}

export function usePoints() {
  return useQuery({ queryKey: ['signal-ops', 'points'], queryFn: getPoints });
}

/** Set one signal's points or one setting. Errors stay on the mutation for the caller to show. */
export function useSetPoints() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kind: 'signal'; signalType: string; points: number; rationale: string } | { kind: 'setting'; key: string; value: number; rationale: string }) =>
      v.kind === 'signal' ? setPoints(v.signalType, v.points, v.rationale) : setScoreSetting(v.key, v.value, v.rationale),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['signal-ops', 'points'] });
    },
  });
}
